import { createReducer, on } from '@ngrx/store';
import {
    addNew,
    discardChanges,
    hide,
    selectAll,
    selectGroup, selectNone,
    setSingle,
    show,
    toggleMany,
    toggleSingle
} from '../actions';
import { ContextSelectorState } from '../context-selector.state';
import { Context, ContextSelectorGroup, ContextSelectorSetup } from '../../../../utils/context-selector-setup.util';

const initialState: ContextSelectorState = {
    singlePropertySelector: null,
    multiplePropertySelector: null
};

/*
 * Every handler below used to take a SHALLOW copy (`{...state}`) and then write straight
 * through it — `newState[id].isActive = true`, `contextGroup.totalSelected = …`,
 * `context.isActive = …`. A shallow copy shares the selector, its `contextGroups` array,
 * every group and every context BY REFERENCE, so those writes landed on the objects held by
 * the PREVIOUS state: nothing below the top level ever changed identity, and the devtools
 * history was rewritten retroactively.
 *
 * That is not theoretical here. `AppEffects.onStartup$` (app/store/effects/app.effects.ts:73-76)
 * dispatches `addNew` ×2 and `selectGroup` ×2 on every authenticated, non-collaborative
 * session, so the `selectGroup` mutation ran on EVERY BOOT and would throw the moment
 * `strictStateImmutability` is turned on.
 *
 * Two constraints shaped the rewrite:
 *
 * 1. The state holds CLASS INSTANCES (`ContextSelectorSetup` / `ContextSelectorGroup` /
 *    `Context`), not object literals. A plain `{...instance}` spread would silently
 *    downgrade them to prototype-less objects, so every clone goes through `cloneInstance`,
 *    which carries the prototype across.
 * 2. `.map(group => group.contexts).reduce(i => i)` — used by the group handlers — has no
 *    seed, so it returns the FIRST matching group's `contexts` array, not a concatenation.
 *    Only that group's contexts were ever toggled, while the `totalSelected` recount ran
 *    over ALL matching groups. That asymmetry is preserved deliberately below.
 */

/** Shallow clone that keeps the prototype — the state holds class instances. */
function cloneInstance<T extends object>(instance: T, patch?: Partial<T>): T {
    return Object.assign(Object.create(Object.getPrototypeOf(instance)), instance, patch);
}

/**
 * Rebuild one selector in the dictionary. Note the state is keyed by selector id
 * (`addNew` writes `[action.payload.id]: action.payload`), not by the two fixed keys
 * `ContextSelectorState` declares.
 *
 * A missing id still throws inside `project`, exactly as the previous
 * `newState[id].isActive = …` did.
 */
function withSelector(
    state: ContextSelectorState,
    contextSelectorId: string,
    project: (setup: ContextSelectorSetup) => ContextSelectorSetup
): ContextSelectorState {
    return {
        ...state,
        [contextSelectorId]: project((state as any)[contextSelectorId])
    } as ContextSelectorState;
}

/** Replace a selector's groups wholesale. Groups the projector returns unchanged keep their reference. */
function withGroups(
    setup: ContextSelectorSetup,
    project: (group: ContextSelectorGroup, index: number) => ContextSelectorGroup
): ContextSelectorSetup {
    return cloneInstance(setup, { contextGroups: setup.contextGroups.map(project) });
}

/** Index of the first group with this name — the one `.reduce(i => i)` used to single out. */
const firstGroupIndex = (setup: ContextSelectorSetup, groupName: string) =>
    setup.contextGroups.findIndex(group => group.name === groupName);

const countActive = (contexts: Context[]) => contexts.filter(context => context.isActive).length;

/**
 * Apply `mapContexts` to the FIRST group matching `groupName`, then recount `totalSelected`
 * on EVERY matching group — reproducing the original reduce/forEach split.
 */
function withGroupContexts(
    setup: ContextSelectorSetup,
    groupName: string,
    mapContexts: (contexts: Context[]) => Context[],
    recount: boolean
): ContextSelectorSetup {
    const target = firstGroupIndex(setup, groupName);
    return withGroups(setup, (group, index) => {
        const isMatch = group.name === groupName;
        const contexts = index === target ? mapContexts(group.contexts) : group.contexts;
        if (contexts === group.contexts && !(isMatch && recount)) {
            return group;
        }
        const patch: Partial<ContextSelectorGroup> = { contexts };
        if (isMatch && recount) {
            patch.totalSelected = countActive(contexts);
        }
        return cloneInstance(group, patch);
    });
}

/** Set `isActive` on each context from a predicate, reusing the instance when nothing changes. */
const setActiveBy = (predicate: (context: Context) => boolean) => (contexts: Context[]) =>
    contexts.map(context => {
        const isActive = predicate(context);
        return context.isActive === isActive ? context : cloneInstance(context, { isActive });
    });

export const contextSelectorReducer = createReducer(initialState,
    on(addNew, (state, action) => ({
        ...state,
        [action.payload.id]: action.payload
    })),
    on(show, (state, action) => withSelector(state, action.payload.contextSelectorId, setup =>
        cloneInstance(withGroups(setup, group => {
            const initialActiveContextIds = group.contexts
                .filter(context => context.isActive)
                .map(context => context._id);
            return cloneInstance(group, {
                initialActiveContextIds,
                totalSelected: initialActiveContextIds.length
            });
        }), { isActive: true })
    )),
    on(discardChanges, (state, action) => withSelector(state, action.payload.contextSelectorId, setup =>
        withGroups(setup, group => {
            const contexts = setActiveBy(context =>
                !!group.initialActiveContextIds.find(id => context._id === id))(group.contexts);
            return contexts === group.contexts ? group : cloneInstance(group, { contexts });
        })
    )),
    on(hide, (state, action) => withSelector(state, action.payload.contextSelectorId, setup =>
        cloneInstance(setup, { isActive: false })
    )),
    on(selectGroup, (state, action) => withSelector(state, action.payload.contextSelectorId, setup =>
        withGroups(setup, group =>
            group.name === action.payload.groupName ? cloneInstance(group, { isActive: true }) : group)
    )),
    on(toggleMany, (state, action) => withSelector(state, action.payload.contextSelectorId, setup =>
        withGroupContexts(
            setup,
            action.payload.groupName,
            contexts => contexts.map(context =>
                context._id === action.payload.contextId
                    ? cloneInstance(context, { isActive: !context.isActive })
                    : context),
            true)
    )),
    on(toggleSingle, (state, action) => withSelector(state, action.payload.contextSelectorId, setup =>
        withGroupContexts(
            setup,
            action.payload.groupName,
            contexts => contexts.map(context =>
                context._id === action.payload.contextId
                    ? cloneInstance(context, { isActive: !context.isActive })
                    : (context.isActive ? cloneInstance(context, { isActive: false }) : context)),
            false)
    )),
    on(setSingle, (state, action) => withSelector(state, action.payload.contextSelectorId, setup =>
        withGroupContexts(
            setup,
            action.payload.groupName,
            setActiveBy(context => context._id === action.payload.contextId),
            false)
    )),
    on(selectAll, (state, action) => withSelector(state, action.payload.contextSelectorId, setup =>
        withGroupContexts(setup, action.payload.groupName, setActiveBy(() => true), true)
    )),
    on(selectNone, (state, action) => withSelector(state, action.payload.contextSelectorId, setup =>
        // The original zeroed `totalSelected` outright rather than recounting; with every
        // context deactivated the recount yields 0 anyway, so this is value-identical.
        withGroupContexts(setup, action.payload.groupName, setActiveBy(() => false), true)
    ))
);
